/**
 * client/src/features/income/services/bucketService.js
 * 
 * Service for bucket-related API interactions.
 */
import api from '../../../utils/api';
import { logApiCall, logApiError } from './api';

// Base API URL for MongoDB integration
const API_URL = '/api/buckets';

/**
 * Get all buckets (including partner buckets if includePartner is true)
 * @param {Object} options - Options including partnerAccess flag and includePartner flag
 * @returns {Promise} Promise with bucket data
 */
const getAll = async (options = {}) => {
  try {
    console.log('🔄 Fetching all buckets...', options);
    
    let response;
    if (options.partnerAccess) {
      // Use cashflow endpoint for partner access with query parameter (avoids CORS issues)
      console.log('🤝 Using cashflow endpoint for partner access');
      response = await api.get('/api/cashflow/buckets?partner=true');
    } else {
      // Use regular endpoint for user's own data
      const url = options.includePartner ? `${API_URL}?includePartner=true` : API_URL;
      response = await api.get(url);
    }
    
    logApiCall('GET', options.partnerAccess ? '/api/cashflow/buckets' : API_URL, null, response.data);
    return response;
  } catch (err) {
    console.error('❌ Error fetching buckets:', err);
    logApiError('GET', options.partnerAccess ? '/api/cashflow/buckets' : API_URL, null, err);
    throw err;
  }
};

/**
 * Get a specific bucket by ID
 * @param {string} id - Bucket ID
 * @returns {Promise} Promise with bucket data
 */
const getById = async (id) => {
  console.log(`🔄 Getting bucket ${id}...`);
  
  try {
    const response = await api.get(`${API_URL}/${id}`);
    const data = response.data;
    logApiCall('GET', `${API_URL}/${id}`, { id }, data);
    return { data };
  } catch (err) {
    console.error(`❌ Error fetching bucket ${id}:`, err);
    logApiError('GET', `${API_URL}/${id}`, { id }, err);
    throw err;
  }
};

/**
 * Create a new bucket
 * @param {Object} bucketData - New bucket data
 * @returns {Promise} Promise with created bucket data
 */
const create = async (bucketData) => {
  console.log('🔄 Creating new bucket:', bucketData);
  
  try {
    const response = await api.post(API_URL, bucketData);
    console.log('📦 Create bucket response:', response);
    logApiCall('POST', API_URL, bucketData, response.data);
    return { data: response.data };
  } catch (err) {
    console.error('❌ Error creating bucket:', err);
    logApiError('POST', API_URL, bucketData, err);
    throw err;
  }
};

/**
 * Update an existing bucket
 * @param {string} id - Bucket ID
 * @param {Object} bucketData - Updated bucket data
 * @returns {Promise} Promise with updated bucket data
 */
const update = async (id, bucketData) => {
  console.log(`🔄 Updating bucket ${id}:`, bucketData);
  
  try {
    const response = await api.put(`${API_URL}/${id}`, bucketData);
    const data = response.data;
    logApiCall('PUT', `${API_URL}/${id}`, bucketData, data);
    return { data };
  } catch (err) {
    console.error(`❌ Error updating bucket ${id}:`, err);
    logApiError('PUT', `${API_URL}/${id}`, bucketData, err);
    throw err;
  }
};

/**
 * Delete a bucket
 * @param {string} id - Bucket ID
 * @returns {Promise} Promise with deletion confirmation
 */
const remove = async (id) => {
  console.log(`🔄 Deleting bucket ${id}`);
  
  try {
    const response = await api.delete(`${API_URL}/${id}`);
    console.log(`✅ Bucket ${id} deleted successfully:`, response.data);
    logApiCall('DELETE', `${API_URL}/${id}`, { id }, response.data);
    return response;
  } catch (err) {
    console.error(`❌ Error deleting bucket ${id}:`, err);
    logApiError('DELETE', `${API_URL}/${id}`, { id }, err);
    throw err;
  }
};

/**
 * Reorder buckets
 * @param {Array} orderData - Array of objects with bucket ID and new order
 * @returns {Promise} Promise with reordering confirmation
 */
const reorder = async (orderData) => {
  console.log('🔄 Reordering buckets:', orderData);
  
  try {
    const response = await api.put(`${API_URL}/reorder`, { buckets: orderData });
    logApiCall('PUT', `${API_URL}/reorder`, orderData, response.data);
    return response;
  } catch (err) {
    console.error('❌ Error reordering buckets:', err);
    logApiError('PUT', `${API_URL}/reorder`, orderData, err);
    throw err;
  }
};

/**
 * Get income entries for a bucket
 * @param {string} bucketId - Bucket ID
 * @returns {Promise} Promise with income entries for the bucket
 */
const getBucketIncomes = async (bucketId) => {
  console.log(`🔄 Getting incomes for bucket ${bucketId}`);
  
  try {
    const response = await api.get(`${API_URL}/${bucketId}/transactions`);
    logApiCall('GET', `${API_URL}/${bucketId}/transactions`, { bucketId }, response.data);
    return response;
  } catch (err) {
    console.error(`❌ Error getting bucket incomes ${bucketId}:`, err);
    logApiError('GET', `${API_URL}/${bucketId}/transactions`, { bucketId }, err);
    throw err;
  }
};

const bucketService = {
  getAll,
  getById,
  create,
  update,
  remove,
  reorder,
  getBucketIncomes
};

export default bucketService; 